import React, { useEffect, useState } from "react";
import { t } from "i18next";
import { useLocation, useNavigate } from "react-router-dom";

import DialogManageUser from "@/app/models/dialogs/DialogMangeUser";
import UniInput from "@/app/components/common/actions/inputs-b/UniInput";
import { UniDropdown } from "@/app/components/common/actions/inputs-b/UniDropdown";
import Mobilefiled from "@/app/components/common/actions/inputs-b/Mobilefiled";
import Uploadfile from "@/app/components/common/actions/inputs-b/Uploadfile";
import FileCard from "../common/FileCard";
import { ICON_BASE_PATH } from "@/app/utils/config";

import "../ThirdParty.css";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import { RequestConfirmation } from "@/app/components/common/alert/RequestConfirmation";
import { useAdminContext } from "@/app/services/context/AdminContext";
import { useUserDetails } from "@/app/services/context/UserDetailsContext";
import {
  useThirdPartySubFeatureUpdate,
  useSmeFileUpload,
} from "@/app/hooks/useAdminApiHooks";
import { FIELD_ICONS } from "../ThirdPartyFieldIcons";
import { useFileView } from "@/app/hooks/useCommonApi";
import { gxCompanyviewApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import GxNoteBar from "@/app/components/webloading/warning/GxNoteBar";
import RequiredMessage from "@/app/components/webloading/errorHandling/RequiredMessage";
import NoteBarBlue from "@/app/components/webloading/warning/NoteBarBlue";

// --------------------------------------------------------------
// Static field configuration.
// Same shape as ZohoErp: these maps are keyed by the `fieldName`
// value coming back from `compConfigFeatureFeeList`, so the backend
// controls which fields render, while the UI controls how each
// field looks/behaves. Both the POS and E-Commerce sub-features
// return the same fieldName set, so this one config covers both.
// --------------------------------------------------------------

// "POC Contact Number" keeps using the existing Mobilefiled
// component (country-code + number input) instead of UniInput.
const MOBILE_FIELD_NAME = "POC Contact Number";

// Fields prefilled from personalInfo / company lookup rather than
// starting blank.
const POC_CONTACT_NAME_FIELD = "POC Contact Name";
const TRADE_LICENSE_NUMBER_FIELD = "Trade License Number";

// Fields that render as a document upload (FileCard + Uploadfile)
// instead of a text/dropdown input.
const DOCUMENT_FIELD_TYPE = "DOCUMENT";

// Options for any field rendered as a dropdown (none for POS/E-Commerce
// today, kept here so a future backend-added dropdown field "just works").
const DROPDOWN_OPTIONS: Record<string, string[]> = {};

// Tooltip copy per field (empty string = no tooltip).
const FIELD_TOOLTIPS: Record<string, string> = {
  "POC Contact Name": "",
  "Trade License Number": "",
};

// Builds the per-field required-field message, e.g. "Please enter Trade License Number".
const getRequiredFieldMessage = (fieldName: string) =>
  `Please enter ${fieldName}`;

// --------------------------------------------------------------
// FileCard view/download flags.
// FileCard itself is never modified - these two flags only decide
// what props get passed down to it for a document that's already on
// file:
//   CAN_FILE_VIEW=true       -> pass a file source, so FileCard's
//                               view icon shows. Because FileCard ties
//                               "download" to "a source is present",
//                               this always makes download available
//                               too - there is no source-based way to
//                               offer view without download.
//   CAN_FILE_VIEW=false +
//   CAN_FILE_DOWNLOAD=true    -> no source is passed (view stays off),
//                               but an onDownload handler is passed
//                               instead, so only the download icon
//                               shows and it triggers a manual
//                               download built from the same data.
//   both false                -> neither prop is passed; no icons.
// --------------------------------------------------------------
const CAN_FILE_VIEW = false;
const CAN_FILE_DOWNLOAD = false;

interface CompanyViewData {
  vcifId?: string;
  tradeLicenseNumber?: string | null;
  countryCode2?: string | null;
  [key: string]: unknown;
}

interface FileViewItem {
  fileName: string;
  base64Output: string;
  [key: string]: unknown;
}

type ServiceType = "pos" | "e-commerce";

// Per-service copy. Same form/logic underneath, only the dialog
// subtitle and the post-submit confirmation text differ.
const SERVICE_CONFIG: Record<
  ServiceType,
  { subtitleKey: string; successTitle: string; successMessage: string }
> = {
  pos: {
    subtitleKey: "pos",
    successTitle: "Your POS request has been submitted",
    successMessage:
      "Thank you for choosing GrowX. Our partner, RFM Loyalty, will reach out to you at your registered contact number soon with further details regarding your POS request.",
  },
  "e-commerce": {
    subtitleKey: "E-Commerce",
    successTitle: "Your E-Commerce request has been submitted",
    successMessage:
      "Thank you for choosing GrowX. Our partner, RFM Loyalty, will reach out to you at your registered contact number soon with further details regarding your E-Commerce request.",
  },
};

// Resolves which service variant this render is for from the current
// route, so the same component works for both:
//   admin-menu/manage-thirdparty-services/pos-ecommerce/pos
//   admin-menu/manage-thirdparty-services/payment-gateway/e-commerce
const resolveServiceType = (pathname: string): ServiceType =>
  pathname.includes("e-commerce") ? "e-commerce" : "pos";

// Some sub-features need a yes/no gate before the connect form itself
// is shown (e.g. "do you already have an account with the partner?").
// Keyed by subFeatureId so more sub-features can be added here later
// without touching the gating logic itself.
const PRE_CONNECT_CONFIRMATION: Record<
  number,
  { question: string; declineTitle: string }
> = {
  247: {
    question: "Do you have an account with Zoho?",
    declineTitle:
      "You'll need a Zoho account before connecting. Please create one directly with Zoho, then come back here to connect it to GrowX.",
  },
};

// Builds a data: URL from a raw or already-prefixed base64 string, for
// the manual (view-disabled) download path below.
const toDataUrl = (base64Output: string) =>
  base64Output.startsWith("data:") || base64Output.startsWith("http")
    ? base64Output
    : `data:application/pdf;base64,${base64Output}`;

const ThirdPartyConnectForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const serviceType = resolveServiceType(location.pathname);
  const serviceConfig = SERVICE_CONFIG[serviceType];

  // Text/dropdown field values, keyed by fieldName (everything except
  // the mobile field and document fields).
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  // Existing/uploaded document reference per document fieldName - holds
  // whatever the backend/upload API considers the file's value
  // (URL, path, or id string) so it can be sent back in the payload.
  const [documentValues, setDocumentValues] = useState<Record<string, string>>(
    {},
  );
  // Locally selected File objects per document fieldName, used for the
  // Uploadfile control and for showing upload-in-progress state.
  const [documentFiles, setDocumentFiles] = useState<
    Record<string, File | null>
  >({});

  // Full raw upload-API response per document fieldName, kept around in
  // case anything on the response besides the id is needed later - the
  // id itself is pulled out into `documentValues` right after upload.
  const [documentUploadResponses, setDocumentUploadResponses] = useState<
    Record<string, any>
  >({});

  // Mobile field keeps its own state shape to match Mobilefiled's API.
  const [mobile, setMobile] = useState("");
  const [mobileDialCode, setMobileDialCode] = useState("+91");

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  // NEW: surfaces a failed upload/save-changes API call via
  // RequiredMessage - errorCode takes priority (resolved through
  // gxGetErrorMessage inside RequiredMessage itself), falling back to the
  // raw errorMessage from the API when no errorCode comes back.
  const [apiError, setApiError] = useState<{
    errorCode?: string | number | null;
    message?: string | null;
  }>({});
  // NEW: per-document-field "uploaded successfully" confirmation, shown
  // under that field's Uploadfile control right after a successful
  // upload.
  const [uploadSuccessMessages, setUploadSuccessMessages] = useState<
    Record<string, string>
  >({});
  // Answer to the pre-connect yes/no gate (see PRE_CONNECT_CONFIRMATION).
  // "pending" until the user picks Yes/No; resets whenever the selected
  // sub-feature changes.
  const [preConnectAnswer, setPreConnectAnswer] = useState<
    "pending" | "yes" | "no"
  >("pending");

  // Company lookup (for Trade License Number prefill and vcifId used by
  // the document-status check below).
  const [company, setCompany] = useState<CompanyViewData | null>(null);

  // Per-document-field result of the "already on file?" check via
  // useFileView. undefined = not checked yet, null = checked and not
  // found, object = found (data used to render the existing-file card).
  const [documentStatus, setDocumentStatus] = useState<
    Record<string, FileViewItem | null>
  >({});

  const { selectedThirdPartySubFeature } = useAdminContext();
  const { personalInfo, fetchPersonalInfo } = useUserDetails();
  const { mutate: updateThirdPartySubFeature } =
    useThirdPartySubFeatureUpdate();
  const { mutate: uploadSmeFile } = useSmeFileUpload();
  const { mutate: viewFile } = useFileView();

  // "Requested" (P) and "Connected" (A) sub-features are read-only here -
  // the user is just viewing what was already submitted/connected, so the
  // fields get locked and the footer (Submit) is hidden. Only a fresh,
  // not-yet-requested sub-feature is editable.
  const recordState = selectedThirdPartySubFeature?.recordState;
  const isConnected = recordState === "A";
  const isReadOnly = isConnected;

  // subFeatureId can come straight off the selected sub-feature, or (if
  // that's not populated) off the first entry in its field list - both
  // carry the same subFeatureId per the API shape.
  const subFeatureId =
    selectedThirdPartySubFeature?.subFeatureId ??
    selectedThirdPartySubFeature?.compConfigFeatureFeeList?.[0]?.subFeatureId;
  const preConnectConfig = subFeatureId
    ? PRE_CONNECT_CONFIRMATION[subFeatureId]
    : undefined;

  // Re-ask the gate every time a different sub-feature is opened.
  useEffect(() => {
    setPreConnectAnswer("pending");
  }, [selectedThirdPartySubFeature]);

  useEffect(() => {
    fetchPersonalInfo();
  }, []);

  // Looks up the company record (trade license number, vcifId) once
  // personalInfo has resolved a portalCompanyId.
  const getCompanyApi = async () => {
    if (!personalInfo?.portalCompanyId) return;
    setIsLoading(true);
    try {
      const requestPayload = { type: personalInfo?.portalCompanyId };
      const response = await gxCompanyviewApi(requestPayload);
      if (response?.status === 1 && response?.data) {
        setCompany(response.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getCompanyApi();
  }, [personalInfo?.portalCompanyId]);

  // Seed field values from the backend-driven config list whenever the
  // selected sub-feature changes, prefilling POC Contact Name / Number
  // from personalInfo and Trade License Number from the company lookup.
  // Errors/files are cleared on every re-seed so stale validation
  // messages don't survive a feature switch.
  useEffect(() => {
    const list = selectedThirdPartySubFeature?.compConfigFeatureFeeList ?? [];
    const initialFields: Record<string, string> = {};
    const initialDocs: Record<string, string> = {};

    list.forEach((field) => {
      const value = field.fieldValue ?? "";

      if (field.fieldType === DOCUMENT_FIELD_TYPE) {
        initialDocs[field.fieldName] = value;
        return;
      }

      if (field.fieldName === MOBILE_FIELD_NAME) {
        // Mobile value is managed separately via `mobile`/`mobileDialCode`
        // state below, not through fieldValues.
        return;
      }

      if (field.fieldName === POC_CONTACT_NAME_FIELD) {
        initialFields[field.fieldName] = personalInfo?.displayName ?? value;
        return;
      }

      if (field.fieldName === TRADE_LICENSE_NUMBER_FIELD) {
        initialFields[field.fieldName] = company?.tradeLicenseNumber ?? value;
        return;
      }

      initialFields[field.fieldName] = value;
    });

    setFieldValues(initialFields);
    setDocumentValues(initialDocs);
    setDocumentFiles({});
    setFieldErrors({});
    setMobile(personalInfo?.mobileNo ?? "");
    setMobileDialCode(personalInfo?.mobileCode ?? "+91");
  }, [selectedThirdPartySubFeature, personalInfo, company]);

  // Checks each DOCUMENT field against the system via useFileView, keyed
  // by fieldName as documentType and the looked-up company's vcifId.
  useEffect(() => {
    const vcifId = company?.vcifId;
    const list = selectedThirdPartySubFeature?.compConfigFeatureFeeList ?? [];
    const docFields = list.filter(
      (field) => field.fieldType === DOCUMENT_FIELD_TYPE,
    );

    if (!vcifId || docFields.length === 0) return;

    docFields.forEach(async (field) => {
      try {
        const response = await viewFile({
          documentType: field.fieldName.toUpperCase(),
          vcifId,
        });

        if (response?.status === 1 && response?.data?.length) {
          const [fileData] = response.data as FileViewItem[];
          setDocumentStatus((prev) => ({
            ...prev,
            [field.fieldName]: fileData,
          }));
        } else {
          setDocumentStatus((prev) => ({
            ...prev,
            [field.fieldName]: null,
          }));
        }
      } catch (error) {
        console.error(error);
        setDocumentStatus((prev) => ({ ...prev, [field.fieldName]: null }));
      }
    });
  }, [company?.vcifId, selectedThirdPartySubFeature]);

  // Updates a single text/dropdown field's value and clears its error as
  // soon as the user starts fixing it.
  const handleFieldChange = (name: string, val: string) => {
    setFieldValues((prev) => ({ ...prev, [name]: val }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

 const renderStatus = (approved: boolean, isLocallySelected?: boolean) => {
  if (approved) {
    return (
      <>
        <img src={`${ICON_BASE_PATH}active-tick.svg`} alt="Active Tick" />
        <span className="gx-p12-sb text-gray-700">File Already in System</span>
      </>
    );
  }

  if (isLocallySelected) {
    return (
      <>
        <img src={`${ICON_BASE_PATH}info-orange.svg`} alt="Info Orange" />
        <span className="gx-p12-sb text-gray-700">File Selected - Ready to Upload</span>
      </>
    );
  }

  return (
    <>
      <img src={`${ICON_BASE_PATH}info-orange.svg`} alt="Info Orange" />
      <span className="gx-p12-sb text-gray-700">Required to Upload</span>
    </>
  );
};


  // Stores the picked file locally only. The actual upload API call is
  // deferred until Submit (see uploadDocument + handleSubmit).
  const handleDocumentSelect = (fieldName: string, file: File | null) => {
    if (!file) return;

    setDocumentFiles((prev) => ({ ...prev, [fieldName]: file }));
    setFieldErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
    // NEW: clear any stale "uploaded successfully" message left over from
    // a previous file for this field once a new one is picked.
    setUploadSuccessMessages((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  // NEW: clears an already-on-file document (documentStatus) so the row
  // falls back to "not on file", which makes the existing
  // `!isAlreadyOnFile` condition below show the Uploadfile control again -
  // wired to FileCard's new onRemove ("x" button).
  const handleRemoveDocument = (fieldName: string) => {
    setDocumentStatus((prev) => ({ ...prev, [fieldName]: null }));
    setDocumentValues((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
    setDocumentFiles((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
    setUploadSuccessMessages((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  // Uploads a document for a given fieldName via the SME file upload API.
  // `type` is the only value still hardcoded. vcifId and countryCode2
  // both come from the same company lookup (`company`) that vcifId was
  // already sourced from elsewhere in this component, and documentType
  // is derived from the field's own name, matching the same
  // `field.fieldName.toUpperCase()` convention used by the useFileView
  // check above.
  const uploadDocument = async (
    fieldName: string,
    file: File,
  ): Promise<string | null> => {
    setUploadingKey(fieldName);
    try {
      const formData = new FormData();
      formData.append("type", "update");
      formData.append("vcifId", company?.vcifId ?? "");
      // formData.append("documentType", fieldName.toUpperCase());

      formData.append(
        "documentType",
        fieldName === "Shareholder Document"
          ? "SHARE HOLDER DOCUMENT"
          : fieldName.toUpperCase(),
      );

      formData.append("file", file);
      formData.append("countryCode2", company?.countryCode2 ?? "");

      const responseData = await uploadSmeFile(formData);

      // Keep the full response around per field too, in case something
      // on it besides the id is needed later.
      setDocumentUploadResponses((prev) => ({
        ...prev,
        [fieldName]: responseData,
      }));

      // NEW: upload API responded but the call itself failed (e.g.
      // { data: null, status: 0, errorCode: "1727", errorMessage:
      // "RECORD_NOT_FOUND" }). Surface it via RequiredMessage - errorCode
      // takes priority, falling back to errorMessage when no errorCode
      // comes back - and treat this document as not uploaded.
      if (!responseData?.data) {
        setApiError({
          errorCode: responseData?.errorCode ?? null,
          message:
            responseData?.errorMessage ||
            `Failed to upload ${fieldName}. Please try again.`,
        });
        setFieldErrors((prev) => ({
          ...prev,
          [fieldName]: `Failed to upload ${fieldName}`,
        }));
        return null;
      }

      // TODO: adjust this to match the actual upload response shape.
      const uploadedId = responseData?.data?.id ?? null;

      if (uploadedId) {
        setDocumentValues((prev) => ({ ...prev, [fieldName]: uploadedId }));
        // NEW: per-file success confirmation, shown under the Uploadfile
        // control for this field.
        setUploadSuccessMessages((prev) => ({
          ...prev,
          [fieldName]: `${file.name} uploaded successfully`,
        }));
      }
      return uploadedId;
    } catch (error) {
      console.error(error);
      setFieldErrors((prev) => ({
        ...prev,
        [fieldName]: `Failed to upload ${fieldName}`,
      }));
      setApiError({
        errorCode: null,
        message: `Failed to upload ${fieldName}. Please try again.`,
      });
      return null;
    } finally {
      setUploadingKey(null);
    }
  };

  const handleDownloadDocument = async (fieldName: string) => {
    const src = documentValues[fieldName];
    if (!src) return;

    setDownloadingKey(fieldName);
    try {
      const link = document.createElement("a");
      link.href = src;
      link.download = fieldName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      setDownloadingKey(null);
    }
  };

  // Manual download for the view-disabled / download-only path: builds a
  // link from the useFileView payload directly, since no file source is
  // being handed to FileCard in that mode.
  const handleDownloadRecordedDocument = (fileOnRecord: FileViewItem) => {
    const link = document.createElement("a");
    link.href = toDataUrl(fileOnRecord.base64Output);
    link.download = fileOnRecord.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Checks every field currently rendered and returns a map of
  // fieldName -> error message for any field that's empty/missing.
  const validateRequiredFields = (): Record<string, string> => {
    const list = selectedThirdPartySubFeature?.compConfigFeatureFeeList ?? [];
    const errors: Record<string, string> = {};

    list.forEach((field) => {
      if (field.fieldType === DOCUMENT_FIELD_TYPE) {
        const alreadyOnFile = Boolean(documentStatus[field.fieldName]);
        if (
          !alreadyOnFile &&
          !documentValues[field.fieldName] &&
          !documentFiles[field.fieldName]
        ) {
          errors[field.fieldName] = `Please upload ${field.fieldName}`;
        }
        return;
      }

      if (field.fieldName === MOBILE_FIELD_NAME) {
        if (!mobile.trim()) {
          errors[field.fieldName] = getRequiredFieldMessage(field.fieldName);
        }
        return;
      }

      const value = fieldValues[field.fieldName] ?? "";
      if (!value.trim()) {
        errors[field.fieldName] = getRequiredFieldMessage(field.fieldName);
      }
    });

    return errors;
  };

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    // Read-only view (already Requested/Connected) - nothing to submit.
    if (isReadOnly) return;

    if (!selectedThirdPartySubFeature) {
      console.error(
        "[ThirdPartyConnectForm] No selectedThirdPartySubFeature in context - cannot submit.",
      );
      return;
    }

    const errors = validateRequiredFields();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    // NEW: clear any error from a previous failed attempt before retrying.
    setApiError({});

    try {
      setIsLoading(true);

      // Upload any newly-selected documents now (in parallel with each
      // other, but this whole block is awaited before anything below
      // it runs), so each document's id from its upload response is
      // already known before the save-changes payload is built and
      // the update API is called. NEW: each upload's own success/failure
      // is tracked (uploadDocument already sets fieldErrors/apiError for
      // whichever one(s) failed) so the save-changes call further below
      // only fires once every selected document has uploaded successfully.
      const uploadedDocs: Record<string, string> = {};
      const uploadResults = await Promise.all(
        documentFields.map(async (field) => {
          const file = documentFiles[field.fieldName];
          if (!file) return true;

          const uploadedRef = await uploadDocument(field.fieldName, file);
          if (uploadedRef) {
            uploadedDocs[field.fieldName] = uploadedRef;
            return true;
          }
          return false;
        }),
      );

      if (uploadResults.some((success) => !success)) {
        // At least one document failed to upload - stop here without
        // calling the save-changes API. The per-field error and apiError
        // set inside uploadDocument already explain why.
        return;
      }

      // Build the update payload, preserving backend identifiers and only
      // overriding fieldValue with what the user typed/uploaded.
      const updatedCompConfigFeatureFeeList = (
        selectedThirdPartySubFeature.compConfigFeatureFeeList ?? []
      ).map((field) => {
        let fieldValue = fieldValues[field.fieldName] ?? field.fieldValue;

        if (field.fieldType === DOCUMENT_FIELD_TYPE) {
          fieldValue =
            uploadedDocs[field.fieldName] ??
            documentValues[field.fieldName] ??
            field.fieldValue;
        } else if (field.fieldName === MOBILE_FIELD_NAME) {
          fieldValue = `${mobileDialCode}${mobile}`;
        }

        return {
          smeConfFeatureFeeId: field.smeConfFeatureFeeId,
          smeFeatureFeeId: field.smeFeatureFeeId,
          groupSmeCompId: selectedThirdPartySubFeature.groupSmeCompId,
          subFeatureId: field.subFeatureId,
          subFeatureConfigId: field.subFeatureConfigId,
          cifId: selectedThirdPartySubFeature.cifId ?? null,
          fieldName: field.fieldName,
          fieldValue,
          redirectUrl: field.redirectUrl,
        };
      });

      const payload = {
        smeFeatureFeeId: selectedThirdPartySubFeature.smeFeatureFeeId,
        featureId: selectedThirdPartySubFeature.featureId,
        paramConfigFlag: selectedThirdPartySubFeature.paramConfigFlag,
        compConfigFeatureFeeList: updatedCompConfigFeatureFeeList,
      };

      const responseData = await updateThirdPartySubFeature(payload);

      if (responseData?.data) {
        setShowSubmitted(true);
      } else {
        // NEW: save-changes call responded but failed - same
        // errorCode-first, errorMessage-fallback pattern as uploadDocument.
        setApiError({
          errorCode: responseData?.errorCode ?? null,
          message:
            responseData?.errorMessage ||
            "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      console.error(error);
      // NEW: request itself failed (network/exception) - no errorCode to
      // resolve, so fall back straight to a generic message.
      setApiError({
        errorCode: null,
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    navigate(-1);
  };

  const handleCloseSubmitted = () => {
    setShowSubmitted(false);
    navigate(-1);
  };

  // Split the backend-driven field list into mobile / document / other,
  // so they can render with the right control.
  const allFields =
    selectedThirdPartySubFeature?.compConfigFeatureFeeList ?? [];
  const mobileField = allFields.find(
    (field) => field.fieldName === MOBILE_FIELD_NAME,
  );
  const documentFields = allFields.filter(
    (field) => field.fieldType === DOCUMENT_FIELD_TYPE,
  );
  const otherFields = allFields.filter(
    (field) =>
      field.fieldName !== MOBILE_FIELD_NAME &&
      field.fieldType !== DOCUMENT_FIELD_TYPE,
  );

  const getMaxLength = (
    field: (typeof allFields)[number] | undefined,
    fallback: number,
  ) =>
    field?.fieldLength && field.fieldLength > 0 ? field.fieldLength : fallback;

  // Whether the pre-connect gate is fully cleared (no config for this
  // sub-feature, it's read-only/already connected, or the user already
  // answered Yes) - only then does the actual connect form render.
  const preConnectCleared =
    !preConnectConfig || isReadOnly || preConnectAnswer === "yes";

  return (
    <div className="gx-erp-container">
      {isLoading && <SubmitLoading />}

      {preConnectConfig && !isReadOnly && preConnectAnswer === "pending" && (
        <RequestConfirmation
          actionType="reset"
          title={preConnectConfig.question}
          message=""
          cancelLabel="No"
          confirmLabel="Yes"
          onConfirm={() => setPreConnectAnswer("yes")}
          onClose={() => setPreConnectAnswer("no")}
        />
      )}

      {preConnectConfig && !isReadOnly && preConnectAnswer === "no" && (
        <RequestConfirmation
          actionType="submitted"
          title={preConnectConfig.declineTitle}
          message=""
          onConfirm={handleCloseModal}
          onClose={handleCloseModal}
        />
      )}

      {preConnectCleared && (
        <>
          {showSubmitted && (
            <RequestConfirmation
              actionType="submitted"
              onConfirm={() => handleCloseSubmitted()}
              onClose={handleCloseSubmitted}
              title={serviceConfig.successTitle}
              message={serviceConfig.successMessage}
            />
          )}

          <DialogManageUser
            isOpen={true}
            handleHeaderClose={handleCloseModal}
            handleFooterSubmit={handleSubmit}
            title={t("request")}
            subtitle={t(serviceConfig.subtitleKey)}
            submit={t("submit_request")}
            cancel={""}
            userRoll=""
            userStatus=""
            handleAcitiveUsers={() => console.log("Activate users")}
            hideFooter={isReadOnly}
          >
            <div className="gx-third-party-form-wrapper">
              <form className="gx-third-party-form" onSubmit={handleSubmit}>
                <div className="gx-third-party-form-grid">
                  {mobileField && (
                    <Mobilefiled
                      label={mobileField.fieldName}
                      value={mobile}
                      onChange={setMobile}
                      onCountryCodeChange={setMobileDialCode}
                      countryDialCode={mobileDialCode}
                      onValidate={() => console.log("Handle validate")}
                      fieldName="mobile"
                      mobileError={fieldErrors[mobileField.fieldName]}
                      disable={isReadOnly}
                    />
                  )}

                  {otherFields.map((field) => {
                    const commonProps = {
                      key: field.subFeatureConfigId,
                      fieldName: field.fieldName,
                      label: field.fieldName,
                      value: fieldValues[field.fieldName] ?? "",
                      onValidate: (name: string, isValid: boolean) =>
                        console.log(`Validation for ${name}: ${isValid}`),
                      validationMessage: fieldErrors[field.fieldName] ?? "",
                      tooltip: FIELD_TOOLTIPS[field.fieldName],
                      tooltipFloat: "down" as const,
                    };

                    if (field.fieldType === "DROPDOWN") {
                      const dropdownOptions =
                        DROPDOWN_OPTIONS[field.fieldName] ?? [];

                      return (
                        <UniDropdown
                          {...commonProps}
                          onChange={(val) =>
                            handleFieldChange(field.fieldName, val)
                          }
                          data={dropdownOptions}
                          showStarLabel
                          iconSrc={FIELD_ICONS[field.fieldName] ?? "link.svg"}
                          max={getMaxLength(field, 50)}
                          disable={isReadOnly}
                        />
                      );
                    }

                    return (
                      <UniInput
                        {...commonProps}
                        onChange={(val) =>
                          handleFieldChange(field.fieldName, val)
                        }
                        iconFile={FIELD_ICONS[field.fieldName]}
                        minLength={3}
                        max={getMaxLength(field, 40)}
                        disable={isReadOnly}
                      />
                    );
                  })}
                </div>

                {/* Hidden submit lets DialogManageUser's footer button (which
                    calls handleFooterSubmit) trigger native form submission.
                    No-op when read-only since the footer itself is hidden. */}
                <button
                  type="submit"
                  className="gx-hidden-submit"
                  aria-label="Submit form"
                />
              </form>

              <div className="gx-required-documents">
                <p className="gx-p14-sb">Required Documents</p>

                {documentFields.map((field) => {
                  const fileOnRecord = documentStatus[field.fieldName];
                  const localFile = documentFiles[field.fieldName];
                  const isAlreadyOnFile = Boolean(fileOnRecord);
                  const isLocallySelected = Boolean(localFile);

                  // Resolve which props (if any) go to FileCard for this
                  // field, per the CAN_FILE_VIEW / CAN_FILE_DOWNLOAD flags.
                  let fileCardProps: {
                    base64Output?: string;
                    onDownload?: () => void;
                  } = {};

                  if (isAlreadyOnFile && fileOnRecord) {
                    if (CAN_FILE_VIEW) {
                      fileCardProps = {
                        base64Output: fileOnRecord.base64Output,
                      };
                    } else if (CAN_FILE_DOWNLOAD) {
                      fileCardProps = {
                        onDownload: () =>
                          handleDownloadRecordedDocument(fileOnRecord),
                      };
                    }
                  }

                  return (
                    <React.Fragment key={field.subFeatureConfigId}>
                      <FileCard
                        fileTitle={field.fieldName}
                        fileName={fileOnRecord?.fileName ?? localFile?.name ?? field.fieldName}
                        {...fileCardProps}
                        statusContent={renderStatus(isAlreadyOnFile, isLocallySelected)}
                        // NEW: "x" remove button - only when there's an
                        // actual on-file document to remove and the form
                        // isn't read-only. Clears documentStatus for this
                        // field, which flips isAlreadyOnFile to false and
                        // brings the Uploadfile control below back so the
                        // user can upload a replacement.
                        // onRemove={
                        //   !isReadOnly && isAlreadyOnFile
                        //     ? () => handleRemoveDocument(field.fieldName)
                        //     : undefined
                        // }
                      />

                      {!isReadOnly && !isAlreadyOnFile && (
                        <>
                          <Uploadfile
                            value={documentFiles[field.fieldName] ?? null}
                            onChange={(file) =>
                              handleDocumentSelect(field.fieldName, file)
                            }
                            validationMessage={fieldErrors[field.fieldName]}
                            touched={true}
                            label={field.fieldName}
                          />
                          {/* NEW: per-file "uploaded successfully"
                              confirmation, set inside uploadDocument. */}
                          {uploadSuccessMessages[field.fieldName] && (
                            <p className="gx-p12-sb gx-upload-success-message">
                              {uploadSuccessMessages[field.fieldName]}
                            </p>
                          )}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* NEW: surfaces any upload/save-changes API error -
                  errorCode takes priority (resolved inside
                  RequiredMessage), falling back to the raw errorMessage. */}
              <div className="my-4">
                <NoteBarBlue>
                  GrowX Global will share the information and documents you
                  provide in this form with our trusted partner, RFM Royalty,
                  for verification and to process your POS request.
                </NoteBarBlue>
              </div>
              <RequiredMessage
                errorCode={apiError.errorCode}
                message={apiError.message}
              />
            </div>
          </DialogManageUser>
        </>
      )}
    </div>
  );
};

export default ThirdPartyConnectForm;
